/* eslint-disable import/no-extraneous-dependencies */

import { debounce, getScreenSize, appendToUrl } from './util';
import {
    getCountryCode,
    getScreenResolution,
    updateUrl,
    isFirstMobileTile,
    initSwiper,
    adjustMapView,
} from './searchHelpers';
import addProduct from './components/addProduct';
import coordinates from '../../../config/coordinates.json';

require('./components/loader');

const MOBILE_MAX_WIDTH = '(max-width: 900px)';
const DEFAULT_ZOOM_LEVEL = 10;

const MapHero = (() => {
    let mapbox;
    let clusters;
    let selectedClubSwiper;
    let swiperIntersectionObserver;

    /**
     * Adds parameters to the given URL
     * @param {string} url - URL
     * @param {string} q - List of search terms, comma separated
     * @param {string} pid - Store id to fetch a selected store data
     * @param {boolean} isMore - True if the given URL is used to fetch more store tiles for the grid, False otherwise
     * @param {boolean} isCityPage - True if request is executed for the city page, False otherwise
     * @returns {string} - Enriched URL
     */
    const enrichUrl = (url, q, pid, isMore, isCityPage) => {
        const urlParameters = {};

        if (q) {
            urlParameters.q = q;
        }

        if (pid) {
            urlParameters.pid = pid;
        }

        if (isMore) {
            const $grid = $('.js-map-hero-grid');

            urlParameters.s = $grid.attr('data-page-start');
            urlParameters.sz = $grid.attr('data-page-size');
            urlParameters.isLoadMore = isMore;
        }

        if (isCityPage) {
            urlParameters.isCityPage = isCityPage;
        }

        return appendToUrl(url, urlParameters);
    };

    /**
     * Destroys selected club swiper
     */
    const destroySelectedClubSwiper = () => {
        if (typeof selectedClubSwiper === 'undefined') {
            return;
        }

        selectedClubSwiper.destroy({
            cleanStyles: true,
        });
        selectedClubSwiper = undefined;
    };

    /**
     * Removes sentinel HTML element from the map hero PD component
    */
    const removeSentinel = () => {
        const $grid = $('.js-map-hero-grid');

        $grid.find('.js-map-hero-grid-sentinel').each((i, currentSentinel) => {
            $(currentSentinel).remove();
        });
    };

    /**
     * Builds sentinel HTML element for the map hero PD component to be used to initialize Intersection Observer API
     */
    const buildSentinel = () => {
        const $grid = $('.js-map-hero-grid');
        const $backToTop = $('.js-back-to-top');
        const $mapbox = $('.js-map-hero-mapbox');
        const moreUrl = $grid.attr('data-more-url');
        const q = $mapbox.attr('data-location');
        const url = enrichUrl(moreUrl, q, null, true, false);
        const sentinel = document.createElement('li');
        const $loader = $('<li />');

        sentinel.classList.add('sentinel', 'js-map-hero-grid-sentinel');
        $loader.addClass('loadcontainer js-loader');

        $backToTop.before($loader);
        $backToTop.before(sentinel);

        const targetObserver = new IntersectionObserver(entries => {
            if (entries[0].intersectionRatio > 0) {
                targetObserver.disconnect();
                $loader.loader().start();

                $.ajax({
                    url,
                    type: 'GET',
                    dataType: 'json',
                    success: data => {
                        $loader.loader().stop();
                        $loader.remove();

                        $grid.attr('data-page-start', data.pageStart);
                        $grid.attr('data-page-size', data.pageSize);
                        $grid.attr('data-page-complete', data.isComplete);

                        removeSentinel();

                        $backToTop.before(data.storeTilesHtml);
                        updateUrl({
                            s: data.pageStart - data.pageSize,
                            sz: data.pageSize,
                        });

                        const $storeTiles = $grid.find('.js-store-tile');
                        $storeTiles.each((i, tile) => {
                            const $tile = $(tile);
                            const pid = $tile.attr('data-pid');

                            if (data.storeTilesHtml.indexOf(pid) < 0) {
                                return;
                            }

                            swiperIntersectionObserver.observe(tile);
                        });

                        if (data.isComplete) {
                            const $backToTopLink = $backToTop.find('a');
                            $backToTopLink.addClass('active');
                        } else {
                            buildSentinel();
                        }
                    },
                });
            }
        }, {
            threshold: 0.1,
        });

        targetObserver.observe(sentinel);
    };

    /**
     * Initializes Intersection Observer API for the map hero PD component
     */
    const initStoreGrid = () => {
        const $grid = $('.js-map-hero-grid');
        const $tiles = $grid.find('.js-store-tile');
        const $backToTopLink = $('.js-back-to-top a');
        const $showMoreClubs = $('.js-show-more-clubs');
        const isComplete = $grid.attr('data-page-complete') === 'true';
        const firstId = $tiles.eq(0).attr('data-pid');
        const isMobile = getScreenSize(MOBILE_MAX_WIDTH);

        if (isComplete) {
            $backToTopLink.toggleClass('active', $tiles.length > 5);
            $showMoreClubs.removeClass('active');
        }

        if (!isComplete && !isMobile) {
            buildSentinel();
        }

        swiperIntersectionObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const tile = entry.target;
                const $tile = $(tile);

                if (entry.isIntersecting && (entry.intersectionRatio > 0 || isFirstMobileTile($tile, firstId, isMobile))) {
                    if ($tile.hasClass('store-tile--selected')) {
                        selectedClubSwiper = initSwiper($tile, true);
                    } else {
                        initSwiper($tile, false);
                    }

                    swiperIntersectionObserver.unobserve(tile);
                }
            });
        });

        $tiles.each((i, tile) => {
            swiperIntersectionObserver.observe(tile);
        });
    };

    /**
     * Re-initializes store grid after resizing the window for the map hero PD component
     */
    const reinitStoreGrid = () => {
        const $grid = $('.js-map-hero-grid');
        const isComplete = $grid.attr('data-page-complete') === 'true';
        const isMobile = getScreenSize(MOBILE_MAX_WIDTH);

        if (!isComplete) {
            if (isMobile) {
                const $showMoreClubs = $('.js-show-more-clubs');

                removeSentinel();
                $showMoreClubs.addClass('active');
            } else {
                removeSentinel();
                buildSentinel();
            }
        }
    };

    /**
     * Updates club markers on the map
     * @param {?string} id - Store id or null
     */
    const updateClubMarkers = id => {
        if (!clusters || typeof mapbox === 'undefined') {
            return;
        }

        const clustersData = clusters;

        clustersData.features = clusters.features.map(feature => {
            feature.properties.selected = feature.properties.ID === id; // eslint-disable-line no-param-reassign
            return feature;
        });

        mapbox.getSource('clusters').setData(clustersData);
    };

    /**
     * Builds map clusters
     * @param {number} clusterRadius - Cluster radius in px
     */
    const buildClusters = clusterRadius => {
        if (!clusters || typeof mapbox === 'undefined') {
            return;
        }

        mapbox.addSource('clusters', {
            type: 'geojson',
            data: clusters,
            cluster: true,
            clusterMaxZoom: 12,
            clusterRadius,
        });

        mapbox.addLayer({
            id: 'circles',
            type: 'circle',
            source: 'clusters',
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': [
                    'step',
                    ['get', 'point_count'],
                    '#ffffff',
                    100,
                    '#ffffff',
                ],
                'circle-radius': [
                    'step',
                    ['get', 'point_count'],
                    16,
                    100,
                    30,
                ],
                'circle-stroke-color': 'rgba(254, 112, 0, 0.2)',
                'circle-stroke-width': 1,
            },
        });

        mapbox.addLayer({
            id: 'labels',
            type: 'symbol',
            source: 'clusters',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['DIN Offc Pro Bold'],
                'text-size': 12,
                'text-allow-overlap': true,
            },
        });

        mapbox.addLayer({
            id: 'markers',
            type: 'symbol',
            source: 'clusters',
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': [
                    'case',
                    ['get', 'selected'],
                    'bf-marker-selected',
                    'bf-marker',
                ],
                'icon-size': 1,
                'icon-allow-overlap': true,
            },
        }, 'circles');
    };

    /**
     * Initializes map box for the map hero PD component
     */
    const initMapBox = () => {
        const $mapbox = $('.js-map-hero-mapbox');
        const container = $mapbox.attr('id');
        const q = $mapbox.attr('data-location');
        const mapUrl = $mapbox.attr('data-map-url');
        const storeUrl = $mapbox.attr('data-store-url');
        const url = enrichUrl(mapUrl, q, null, false, false);

        $.ajax({
            url,
            type: 'GET',
            dataType: 'json',
            success: data => {
                const countryCode = getCountryCode();
                const resolution = getScreenResolution();
                const countryConfiguration = coordinates && coordinates[countryCode];
                const configuration = countryConfiguration && countryConfiguration[resolution];
                const isMobile = getScreenSize(MOBILE_MAX_WIDTH);
                const padding = isMobile ? 50 : { left: 620 };
                const bbox = new mapboxgl.LngLatBounds(); // eslint-disable-line no-undef
                const bounds = new mapboxgl.LngLatBounds( // eslint-disable-line no-undef
                    new mapboxgl.LngLat(configuration[0], configuration[1]), // eslint-disable-line no-undef
                    new mapboxgl.LngLat(configuration[2], configuration[3]) // eslint-disable-line no-undef
                );
                const customIcons = [
                    {
                        src: data.bfMarker,
                        name: 'bf-marker',
                        width: 30,
                        height: 43,
                    },
                    {
                        src: data.bfMarkerSelected,
                        name: 'bf-marker-selected',
                        width: 34,
                        height: 48,
                    },
                ];

                clusters = data.clusters;

                mapboxgl.accessToken = data.apiKey; // eslint-disable-line no-undef

                mapbox = new mapboxgl.Map({ // eslint-disable-line no-undef
                    container,
                    style: 'mapbox://styles/mapbox/streets-v11',
                    bounds,
                    pitchWithRotate: false,
                    dragRotate: false,
                    id: 'symbols',
                    maxZoom: 16,
                    touchZoomRotate: true,
                    cooperativeGestures: true,
                });

                mapbox.addControl(new mapboxgl.NavigationControl()); // eslint-disable-line no-undef

                if (clusters && clusters.features.length > 0) {
                    clusters.features.forEach(feature => {
                        bbox.extend(feature.geometry.coordinates);
                    });
                    mapbox.fitBounds(bbox, { padding });
                }

                mapbox.on('load', () => {
                    adjustMapView(mapbox);

                    customIcons.forEach(customIcon => {
                        const icon = new Image(customIcon.width, customIcon.height);
                        icon.onload = () => {
                            mapbox.addImage(customIcon.name, icon);

                            if (customIcon.name === 'bf-marker') {
                                buildClusters(countryConfiguration.clusterRadius);
                            }
                        };
                        icon.src = customIcon.src;
                    });

                    mapbox.on('click', e => {
                        const markers = mapbox.queryRenderedFeatures(e.point, {
                            layers: ['markers'],
                        });

                        if (markers.length > 0) {
                            return;
                        }

                        const $selectedStoreWrapper = $('.js-selected-store-wrapper');
                        $selectedStoreWrapper.removeClass('active');

                        setTimeout(() => {
                            updateClubMarkers(null);
                            destroySelectedClubSwiper();
                            $selectedStoreWrapper.html('');
                        }, 500);
                    });

                    mapbox.on('click', 'circles', e => {
                        const circles = mapbox.queryRenderedFeatures(e.point, {
                            layers: ['circles'],
                        });
                        const id = circles[0].properties.cluster_id;

                        mapbox.getSource('clusters').getClusterExpansionZoom(id, clusterExpansionError => {
                            if (clusterExpansionError) {
                                return;
                            }

                            const currentZoom = mapbox.getZoom();
                            const zoom = currentZoom < DEFAULT_ZOOM_LEVEL ? DEFAULT_ZOOM_LEVEL : currentZoom + 2;

                            mapbox.flyTo({
                                center: circles[0].geometry.coordinates,
                                offset: [100, 0],
                                zoom,
                                speed: 0.6,
                            });
                        });
                    });

                    mapbox.on('mouseenter', 'circles', () => {
                        mapbox.getCanvas().style.cursor = 'pointer';
                    });

                    mapbox.on('mouseleave', 'circles', () => {
                        mapbox.getCanvas().style.cursor = '';
                    });

                    mapbox.on('click', 'markers', e => {
                        const circles = mapbox.queryRenderedFeatures(e.point, {
                            layers: ['circles'],
                        });

                        if (circles.length > 0) {
                            return;
                        }

                        const store = e.features[0];
                        const id = store.properties.ID;
                        const action = enrichUrl(storeUrl, null, id, false, true);

                        $.ajax({
                            url: action,
                            type: 'GET',
                            dataType: 'json',
                            success: storeData => {
                                const $selectedStoreWrapper = $('.js-selected-store-wrapper');
                                $selectedStoreWrapper.html(storeData.storeTileHtml);
                                $selectedStoreWrapper.addClass('active');

                                const $selectedTile = $selectedStoreWrapper.find('.js-store-tile');
                                swiperIntersectionObserver.observe($selectedTile[0]);
                            },
                        });

                        updateClubMarkers(id);
                    });

                    mapbox.on('mouseenter', 'markers', () => {
                        mapbox.getCanvas().style.cursor = 'pointer';
                    });

                    mapbox.on('mouseleave', 'markers', () => {
                        mapbox.getCanvas().style.cursor = '';
                    });
                });
            },
        });
    };

    /**
     * Handles facilties toggle event listener
     * @param {Event} e - Event object
     * @param {boolean} showMore - True if View More button is clicked, False otherwise
     */
    const toggleFacilities = (e, showMore) => {
        e.preventDefault();

        const $target = $(e.target);
        const $tile = $target.closest('.js-store-tile');
        const $allFacilities = $tile.find('.js-facilities-list-all');
        const $viewMore = $tile.find('.js-view-more-facilities');
        const $viewLess = $tile.find('.js-view-less-facilities');
        const animationDuration = 200; // Duration for collapse animation

        if (showMore) {
            // Expand logic
            $viewMore.removeClass('show').attr('hidden', true);
            $viewMore.find('button').attr('aria-expanded', 'true');

            $allFacilities.each((index, facility) => {
                setTimeout(() => {
                    $(facility).addClass('show');
                    setTimeout(() => {
                        $(facility).addClass('animate');
                    }, 10); // Delay for visibility before animation
                }, index * 50); // Stagger expand each facility item
            });

            setTimeout(() => {
                $viewLess.addClass('show').removeAttr('hidden');
            }, $allFacilities.length * 50); // Delay for "view less" button to appear
        } else {
            // Collapse logic - reverse the order of items
            $viewLess.removeClass('show').attr('hidden', true);

            $($allFacilities.get().reverse()).each((index, facility) => {
                setTimeout(() => {
                    $(facility).removeClass('animate'); // Start collapse animation
                    setTimeout(() => {
                        $(facility).removeClass('show'); // Hide element after animation
                    }, animationDuration); // Wait for collapse animation to complete
                }, index * 50); // Stagger collapse for each facility item (starting from the last)
            });

            setTimeout(() => {
                $viewMore.addClass('show').removeAttr('hidden');
                $viewMore.find('button').attr('aria-expanded', 'false');
            }, $allFacilities.length * 50); // Delay for "view more" button to appear
        }
    };

    /**
     * Initializes event listeners for the map hero PD component
     */
    const initEventListeners = () => {
        const $body = $('body');
        const $showMoreClubs = $('.js-show-more-clubs');

        $body.on('click', '.js-store-tile-lead-gen-club, .js-store-tile-presale-club', e => {
            e.preventDefault();

            const $target = $(e.target);
            let $tile = $target.closest('.js-store-tile');

            if ($tile.length < 1) {
                $tile = $target.closest('.js-store-tile-suggestions');
            }

            const storeId = $tile.attr('data-pid');
            const url = $tile.hasClass('js-store-tile-lead-gen-club') ? $tile.attr('data-lead-gen-url') : $tile.attr('data-pre-sale-url');

            window.location = `${url}?sid=${storeId}`;
        });

        $body.on('click', '.js-join', (e) => {
            e.preventDefault();

            const $target = $(e.target);
            const $tile = $target.closest('.js-store-tile');
            const storeId = $tile.attr('data-pid');
            const membershipId = $target.attr('data-membership-id');
            const duration = $target.attr('data-duration');

            addProduct(storeId, membershipId, null, duration);

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
                event: 'precheckout',
                step: '2',
                component_name: 'select club',
            });
        });

        $body.on('click', '.js-back-to-top a', e => {
            e.preventDefault();

            const $grid = $('.js-map-hero-grid');
            const $firstItem = $grid.find('li:not(.hr)').first();
            const $listHeader = $('.map-hero-list-header');
            const $headerHeight = $('.main-page-header').height();
            const $listHeaderPosition = $listHeader.offset().top - $headerHeight;

            $firstItem.get(0).scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });

            $firstItem.focus();

            window.scrollTo({
                top: $listHeaderPosition,
                behavior: 'smooth',
            });
        });

        $showMoreClubs.on('click', e => {
            e.preventDefault();

            const $mapbox = $('.js-map-hero-mapbox');
            const $backToTop = $('.js-back-to-top');
            const $grid = $('.js-map-hero-grid');
            const $loader = $('<li />');
            const moreUrl = $grid.attr('data-more-url');
            const q = $mapbox.attr('data-location');
            const url = enrichUrl(moreUrl, q, null, true, false);

            $loader.addClass('loadcontainer js-loader');
            $loader.loader().start();
            $backToTop.before($loader);

            $.ajax({
                url,
                type: 'GET',
                dataType: 'json',
                success: data => {
                    $loader.loader().stop();
                    $loader.remove();

                    $grid.attr('data-page-start', data.pageStart);
                    $grid.attr('data-page-size', data.pageSize);
                    $grid.attr('data-page-complete', data.isComplete);

                    $backToTop.before(data.storeTilesHtml);
                    updateUrl({
                        s: data.pageStart - data.pageSize,
                        sz: data.pageSize,
                    });

                    const $storeTiles = $grid.find('.js-store-tile');
                    $storeTiles.each((i, tile) => {
                        const $tile = $(tile);
                        const pid = $tile.attr('data-pid');

                        if (data.storeTilesHtml.indexOf(pid) < 0) {
                            return;
                        }

                        swiperIntersectionObserver.observe(tile);
                    });

                    if (data.isComplete) {
                        const $backToTopLink = $backToTop.find('a');

                        $backToTopLink.addClass('active');
                        $showMoreClubs.removeClass('active');
                    }
                },
            });
        });

        $body.on('click', '.js-view-more-facilities', e => toggleFacilities(e, true));
        $body.on('click', '.js-view-less-facilities', e => toggleFacilities(e, false));
    };

    return {
        init() {
            initMapBox();
            initEventListeners();
            initStoreGrid();
        },
        resize() {
            reinitStoreGrid();
        },
    };
})();

export default (() => {
    $(MapHero.init);
    $(window).on('resize', debounce(MapHero.resize, 300));
})();
